package ai.wagemix.web.screens.mixbuilder

import ai.wagemix.data.WMCache
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.mixbuilder.WMDefaultMixBuilderPresenter
import ai.wagemix.logic.mixbuilder.WMFilterOptions
import ai.wagemix.logic.mixbuilder.WMMixBuilderAccess
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Action
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Event
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Prompt
import ai.wagemix.logic.mixbuilder.WMMixBuilderTutorialStep
import ai.wagemix.logic.mixbuilder.WMMixDraftPresentation
import ai.wagemix.logic.mixbuilder.WMPremiumFeature
import ai.wagemix.logic.wages.WMWageSummary
import ai.wagemix.web.components.WMBandChartModel
import ai.wagemix.web.screens.toBandChartModel
import androidx.compose.runtime.State
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewModel
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewStateHolder

interface WMMixBuilderWebViewStateHolder<out P: Prompt>: FSWebPromptingViewStateHolder<P> {
    val draftState: State<WMMixDraftPresentation?>
    val summaryState: State<WMWageSummary?>
    /** What the visitor may do; `null` until the presenter reports it, so nothing renders for the wrong audience. */
    val accessState: State<WMMixBuilderAccess?>
    val chartPrefsState: State<WMCache.Value.ChartPrefs?>
    val filterOptionsState: State<WMFilterOptions>
    /** The open mix is being re-evaluated: it cannot be changed until that finishes. */
    val reevaluatingState: State<Boolean>
    /** The tutorial step to show; `null` when there is none (signed in, finished or skipped). */
    val tutorialState: State<WMMixBuilderTutorialStep?>
    /** The API refused the draft's filter for the plan: a card offers to renew or clear it in place of the chart. */
    val chartLockedState: State<Boolean>
}

abstract class WMBaseMixBuilderWebViewModel<
    P: Prompt,
    E: Event<P>,
    A: Action,
    PR: WMMixBuilderPresenter<P, E, A>
>(
    presenter: PR,
    protected val externalNavigation: (WMNavigationRequest) -> Unit,
    /** The page opens the share dialog for this stored mix; the presenter only asks when the plan includes sharing. */
    protected val onShareRequest: (mixId: String) -> Unit
): FSWebPromptingViewModel<P, E, A, PR>(presenter), WMMixBuilderWebViewStateHolder<P> {

    override val draftState: State<WMMixDraftPresentation?> get() = _draftState
    protected val _draftState = mutableStateOf<WMMixDraftPresentation?>(null)

    override val summaryState: State<WMWageSummary?> get() = _summaryState
    protected val _summaryState = mutableStateOf<WMWageSummary?>(null)

    override val accessState: State<WMMixBuilderAccess?> get() = _accessState
    protected val _accessState = mutableStateOf<WMMixBuilderAccess?>(null)

    override val chartPrefsState: State<WMCache.Value.ChartPrefs?> get() = _chartPrefsState
    protected val _chartPrefsState = mutableStateOf<WMCache.Value.ChartPrefs?>(null)

    override val filterOptionsState: State<WMFilterOptions> get() = _filterOptionsState
    protected val _filterOptionsState = mutableStateOf(WMFilterOptions(emptyList(), emptyList()))

    override val reevaluatingState: State<Boolean> get() = _reevaluatingState
    protected val _reevaluatingState = mutableStateOf(false)

    override val tutorialState: State<WMMixBuilderTutorialStep?> get() = _tutorialState
    protected val _tutorialState = mutableStateOf<WMMixBuilderTutorialStep?>(null)

    override val chartLockedState: State<Boolean> get() = _chartLockedState
    protected val _chartLockedState = mutableStateOf(false)

    /** Formatting only. */
    val chartModelState: State<WMBandChartModel?> = derivedStateOf { _summaryState.value?.toBandChartModel() }

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            draftUpdate?.let { _draftState.value = it }
            summaryUpdate?.let { _summaryState.value = it.value }
            accessUpdate?.let { _accessState.value = it }
            chartPrefsUpdate?.let { _chartPrefsState.value = it }
            filterOptionsUpdate?.let { _filterOptionsState.value = it }
            reevaluatingUpdate?.let { _reevaluatingState.value = it }
            tutorialUpdate?.let { _tutorialState.value = it.value }
            chartLockedUpdate?.let { _chartLockedState.value = it }
            shareRequestUpdate?.let { onShareRequest(it) }
            navigationUpdate?.let { externalNavigation(it) }
        }
    }

    fun userAddedOccupation(occupationId: Int) = presenter.userAddedOccupation(occupationId)
    fun userRemovedOccupation(occupationId: Int) = presenter.userRemovedOccupation(occupationId)
    fun userChangedShare(occupationId: Int, share: Double) = presenter.userChangedShare(occupationId, share)
    fun userCommittedShare(occupationId: Int) = presenter.userCommittedShare(occupationId)
    fun userToggledLock(occupationId: Int) = presenter.userToggledLock(occupationId)
    fun userToggledEnabled(occupationId: Int) = presenter.userToggledEnabled(occupationId)
    fun userChangedFilters(areaId: Int?, industryId: Int?) = presenter.userChangedFilters(areaId, industryId)
    fun userChangedChartPrefs(hourly: Boolean, projectedYears: Int) = presenter.userChangedChartPrefs(hourly, projectedYears)
    fun userRequestedSave() = presenter.userRequestedSave()
    fun userConfirmedSave(title: String) = presenter.userConfirmedSave(title)
    fun userRequestedUpdate() = presenter.userRequestedUpdate()
    fun userRequestedDiscard() = presenter.userRequestedDiscard()
    fun userRequestedPremiumFeature(feature: WMPremiumFeature) = presenter.userRequestedPremiumFeature(feature)
    fun userRequestedReevaluate() = presenter.userRequestedReevaluate()
    fun userRequestedShare() = presenter.userRequestedShare()
    fun userCompletedTutorialStep(step: WMMixBuilderTutorialStep) = presenter.userCompletedTutorialStep(step)
    fun userRequestedSkipTutorial(step: WMMixBuilderTutorialStep) = presenter.userRequestedSkipTutorial(step)
}

typealias WMDefaultMixBuilderWebViewModel = WMBaseMixBuilderWebViewModel<Prompt.Base, Event.Base<Prompt.Base>, Action.Base, WMDefaultMixBuilderPresenter>

fun createDefaultMixBuilderWebViewModel(
    presenter: WMDefaultMixBuilderPresenter,
    externalNavigation: (WMNavigationRequest) -> Unit,
    onShareRequest: (mixId: String) -> Unit
): WMDefaultMixBuilderWebViewModel = object: WMDefaultMixBuilderWebViewModel(presenter, externalNavigation, onShareRequest) {
    override val defaultPrompt: Prompt.Base
        get() = Prompt.baseFactory().none()
}
